import {
  useState,
  useEffect,
  PropsWithChildren,
  CSSProperties,
  ChangeEvent,
  ClipboardEvent,
} from "react";
import { useVerificationCode } from "./helpers";
import "../../../../styles/input.css";
import "./style.css";

type PinInputProps = PropsWithChildren<{
  className?: string;
  pinStyle?: CSSProperties;
  style?: CSSProperties;
  label?: string;
  valid?: boolean;
  validate: (value: number) => void;
  onValid: () => void;
  onReset: () => void;
}>;

export const PinInput = (props: PinInputProps) => {
  const PIN_LENGTH = 4;
  const [value, setValue] = useState<string>("");

  const { refArray, handleSelectInputElement } = useVerificationCode();

  const handleInputClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSelectInputElement();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!value || value.length >= PIN_LENGTH) {
      return;
    }

    if (e.key === "Backspace") {
      if (value?.length > 1) {
        setValue(value?.slice(0, -1))
        refArray[value?.length - 2]?.current?.focus();
      } else {
        setValue("");
        refArray[0]?.current?.focus();
      }
      return;
    }

    if (/^\d+$/.test(e.key)) {
      refArray[value?.length + 1]?.current?.focus();
    }
  };

  const handleChange =
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const valueArray = value
        ? [value.charAt(0), value.charAt(1), value.charAt(2), value.charAt(3)]
        : ["", "", "", ""];
      const inputArray = e.target.value.split("");

      if (inputArray.length > 1) {
        const firstEmptyIndex = valueArray.findIndex((item) => item === "")
        valueArray[firstEmptyIndex] = inputArray[inputArray.length - 1];
      } else {
        for (let i = index; i < PIN_LENGTH; i++) {
          if (inputArray.length > i - index) {
            valueArray[i] = inputArray[i - index];
          }
        }
      }

      const joinedValue = valueArray.join("");
      setValue(joinedValue);
      refArray[joinedValue.length]?.current?.focus();
    };

  const handlePaste = (index: number) => (e: ClipboardEvent) => {
    e.preventDefault();
    if (!value?.charAt(index)) setValue(e.clipboardData?.getData("text"));
  };

  useEffect(() => {
    if (
      value &&
      value.length === PIN_LENGTH &&
      parseInt(value) &&
      !Number.isNaN(parseInt(value))
    ) {
      props.validate(parseInt(value));
    }
  }, [value]);

  useEffect(() => {
    if (props.valid === undefined) {
      return;
    }
    refArray[refArray.length - 1].current?.blur()
    if (props.valid) {
      setTimeout(props.onValid, 2000);
    } else {
      setTimeout(() => {
        props.onReset();
        refArray[0].current?.focus()
        setValue("");
      }, 4000);
    }
  }, [props.valid]);

  useEffect(() => {
    for (let i = 0; i < PIN_LENGTH; i++) {
      const currentRef = refArray[i].current;
      currentRef?.classList.remove("filled", "valid", "invalid");
      if (value.length === 0) {
        continue;
      }

      if (props.valid !== undefined) {
        if (props.valid) {
          currentRef?.classList.add("valid");
        } else if (!props.valid) {
          currentRef?.classList.add("invalid");
        }
        continue;
      }

      if (i <= value.length - 1) {
        currentRef?.classList.add("filled");
      }
    }
  }, [value, props.valid]);

  return (
    <div className={props.className} style={props.style}>
      {props.label && (
        <label className="global--input-label">{props.label}</label>
      )}
      <div className="flex flex-row gap-[32px] sm:gap-0 sm:justify-evenly" style={props.pinStyle}>
        {refArray.map((item, index) => (
          <input
            key={index}
            type="number"
            className="input--pin-input"
            pattern="[0-9]"
            inputMode="numeric"
            ref={item}
            maxLength={1}
            value={value?.charAt(index)}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            onChange={handleChange(index)}
            onPaste={handlePaste(index)}
            autoFocus={index === 0}
          />
        ))}
      </div>
    </div>
  );
};
